import { NgClass, NgStyle } from '@angular/common';
import { Component, inject, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Unit } from '@bn/shared/entities/enums';
import { APP_CONFIG } from '@bn/shared/entities/injection-tokens';
import { FormatBytePipe } from '@bn/shared/entities/pipes';
import { ContentService, UploadFileStatus, UploadManagerService, UploadStateType, UploadSummary } from '@bn/shared/services';
import { ProgressCircleComponent } from '@bn/web-ui/progress-circle/progress-circle.component';

@Component({
  selector: 'bn-web-ui-central-widget',
  templateUrl: './central-widget.component.html',
  styleUrls: ['./central-widget.component.scss'],
  imports: [FaIconComponent, NgClass, NgStyle, FormatBytePipe, ProgressCircleComponent],
})
export class CentralWidgetComponent implements OnDestroy {
  readonly APP_CONFIG = inject(APP_CONFIG);
  readonly contentService = inject(ContentService);
  readonly #uploadManager = inject(UploadManagerService);

  isFileListVisible = true;
  isWidgetCollapsed = false;
  showPercentage = new Map<number, boolean>();
  transitionDuration!: number;
  uploadFileStatuses: UploadFileStatus[] = [];
  uploadSummary: UploadSummary = {
    percentDone: {
      formatted: '0',
      unit: Unit.Bytes,
      value: 0,
    },
    bytesDone: 0,
    bytesTotal: 0,
    counter: {
      items: 0,
      queue: 0,
      complete: 0,
      error: 0,
      inProgress: 0,
      pause: 0,
      resume: 0,
    },
  };
  showProgressBar = false;
  centralWidgetNoProgressBar = false;

  constructor() {
    this.transitionDuration = this.APP_CONFIG.uploadManager.userStatusUpdateMs;
    this.#uploadManager
      .getUploadStatus()
      .pipe(takeUntilDestroyed())
      .subscribe((uploadFileStatuses) => {
        this.uploadFileStatuses = uploadFileStatuses.reverse();
      });

    this.#uploadManager
      .getUploadSummary()
      .pipe(takeUntilDestroyed())
      .subscribe((uploadSummary) => {
        this.uploadSummary = uploadSummary;
        this.centralWidgetNoProgressBar = false;
        // Note: In case when on clear event gets triggered we should not set progress bar back to visible
        this.showProgressBar = !(uploadSummary.bytesDone === 0 && uploadSummary.bytesTotal === 0);
      });
  }

  ngOnDestroy(): void {
    this.showPercentage = new Map();
  }

  progressBarTransitionEnd(event: Event): void {
    if (!(event.target as HTMLElement)?.classList.contains('central-widget__progress-bar__visible')) {
      this.uploadSummary.percentDone.value = 0;
      this.centralWidgetNoProgressBar = true;
    }
  }

  progressBarTrackTransitionEnd(event: Event): void {
    event.stopPropagation();

    if (this.uploadSummary?.percentDone.value >= 100) {
      this.showProgressBar = false;
    }
  }

  // toggleListView() {
  //   this.isFileListVisible = !this.isFileListVisible;
  // }

  onDismissClick(id: string): void {
    this.#uploadManager.clearStatus(id);

    // NOTE: clearStatus will emit event at this.appConfig.uploadManager.userStatusUpdateMs ms.
    // Here we clean completed items immediately
    this.uploadFileStatuses = this.uploadFileStatuses.filter((item) => item.id !== id);
  }

  onClearFinishedClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.#uploadManager.clearStatus();

    // NOTE: clearStatus will emit event at this.appConfig.uploadManager.userStatusUpdateMs ms.
    // Here we clean completed items immediately
    this.uploadFileStatuses = this.uploadFileStatuses.filter((item) => item.state !== 'complete');

    // This hides widget immediately if there is no item with state different than 'complete' (in case when all items are complete)
    if (!this.uploadFileStatuses.length) {
      this.showPercentage = new Map();
    }
  }

  toggleWidget(): void {
    this.isWidgetCollapsed = !this.isWidgetCollapsed;
  }

  onToggleProgressView(index: number, fileState: UploadStateType, showPercent = true): void {
    this.showPercentage.set(index, showPercent || fileState === 'complete');
  }
}
